import { GetStaticProps } from 'next';

import { ContactFieldsFragment, PageInformationFieldsFragment } from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetContact } from 'api/graphql-helper';

import { Head } from 'modules/head/head';
import Layout from 'modules/layout/layout';
import { BaseModule } from 'modules/base-module/base-module';
import IntroHeader from 'modules/intro-header/intro-header';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';

interface IContactPageProps extends IBasePageProps {
	contact?: ContactFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
	id?: string;
}

const ContactPage = (props: IContactPageProps): React.JSX.Element => {
	const { modules } = { ...props?.contact };
	const seoModule = props?.contact?.seo;

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.contact?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<BaseModule
				defaultModuleOptions={{
					backgroundColor: props.contact?.header?.backgroundColor || 'white',
					padding: 'none'
				}}
			>
				<IntroHeader module={props?.contact?.header} slug={ROUTES.CONTACT.fullPath} />
			</BaseModule>
			<ModuleBuilder modules={modules} slug={ROUTES.CONTACT.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IContactPageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetContact(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { pageInformation, contact } = result.props.data;

		if (contact == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				contact: contact.data?.attributes,
				id: contact.data?.documentId,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'contact',
						contentTypeId: contact?.data?.documentId
					}
				}
			}
			// revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in contact page', error);

		return {
			notFound: true
		};
	}
};

export default ContactPage;
